// frontend/src/services/roleService.js
import axios from 'axios';
import { API_URL } from '../config/constants';
import { getTokenAuth } from '../utils/authUtils';

/**
 * Récupérer le token d'authentification
 * @returns {string|null} - Token d'authentification ou null si non trouvé
 */
const getAuthToken = () => {
  return getTokenAuth();
};

/**
 * Service pour gérer les rôles côté client
 */
const roleService = {
  /**
   * Récupérer tous les rôles
   * @param {boolean} includeInactive - Inclure les rôles inactifs
   * @returns {Promise<Array>} - Liste des rôles
   */
  getAllRoles: async (includeInactive = true) => {
    try {
      const token = getAuthToken();
      if (!token) return [];

      const response = await axios.get(
          `${API_URL}/roles`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
      );

      let roles = response.data.roles || [];

      // Filtrer les rôles inactifs si demandé
      if (!includeInactive) {
        roles = roles.filter(role => role.isActive);
      }

      return roles;
    } catch (error) {
      console.error('Error getting roles:', error);
      return [];
    }
  },

  /**
   * Récupérer un rôle par ID
   * @param {string} id - ID du rôle
   * @returns {Promise<Object>} - Rôle récupéré
   */
  getRole: async (id) => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');

      const response = await axios.get(
          `${API_URL}/roles/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
      );

      return response.data.role;
    } catch (error) {
      console.error(`Error getting role ${id}:`, error);
      throw error;
    }
  },

  /**
   * Récupérer un rôle par son nom
   * @param {string} name - Nom du rôle
   * @returns {Promise<Object|null>} - Rôle récupéré ou null si non trouvé
   */
  getRoleByName: async (name) => {
    try {
      const token = getAuthToken();
      if (!token) return null;

      // Récupérer tous les rôles et filtrer par nom
      const roles = await roleService.getAllRoles();
      return roles.find(role => role.name === name) || null;
    } catch (error) {
      console.error(`Error getting role by name ${name}:`, error);
      return null;
    }
  },

  /**
   * Créer un nouveau rôle
   * @param {Object} roleData - Données du rôle
   * @returns {Promise<Object>} - Rôle créé
   */
  createRole: async (roleData) => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');

      // S'assurer que les champs requis sont présents
      const completeRoleData = {
        name: roleData.name,
        description: roleData.description || '',
        isSystem: roleData.isSystem || false,
        isActive: roleData.isActive !== undefined ? roleData.isActive : true,
        displayOrder: roleData.displayOrder || 100,
        icon: roleData.icon || 'Star',
        color: roleData.color || '#607D8B',
        permissions: roleData.permissions || []
      };

      const response = await axios.post(
          `${API_URL}/roles`,
          completeRoleData,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
      );

      return response.data.role;
    } catch (error) {
      console.error('Error creating role:', error);
      throw error;
    }
  },

  /**
   * Mettre à jour un rôle
   * @param {string} id - ID du rôle
   * @param {Object} roleData - Nouvelles données du rôle
   * @returns {Promise<Object>} - Rôle mis à jour
   */
  updateRole: async (id, roleData) => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');

      const response = await axios.patch(
          `${API_URL}/roles/${id}`,
          roleData,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
      );

      return response.data.role;
    } catch (error) {
      console.error(`Error updating role ${id}:`, error);
      throw error;
    }
  },

  /**
   * Supprimer un rôle
   * @param {string} id - ID du rôle
   * @returns {Promise<Object>} - Message de confirmation
   */
  deleteRole: async (id) => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');

      const response = await axios.delete(
          `${API_URL}/roles/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
      );

      return response.data;
    } catch (error) {
      console.error(`Error deleting role ${id}:`, error);
      throw error;
    }
  },

  /**
   * Activer ou désactiver un rôle
   * @param {string} id - ID du rôle
   * @param {boolean} isActive - État d'activation
   * @returns {Promise<Object>} - Rôle mis à jour
   */
  toggleRoleActive: async (id, isActive) => {
    try {
      return await roleService.updateRole(id, { isActive });
    } catch (error) {
      console.error(`Error toggling role active state ${id}:`, error);
      throw error;
    }
  },

  /**
   * Attribuer une permission à un rôle
   * @param {string} roleId - ID du rôle
   * @param {string} permissionId - ID de la permission
   * @returns {Promise<Object>} - Résultat de l'opération
   */
  assignPermissionToRole: async (roleId, permissionId) => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');

      const response = await axios.post(
          `${API_URL}/roles/${roleId}/permissions`,
          { permissionId },
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
      );

      return response.data;
    } catch (error) {
      console.error(`Error assigning permission to role:`, error);
      throw error;
    }
  },

  /**
   * Révoquer une permission d'un rôle
   * @param {string} roleId - ID du rôle
   * @param {string} permissionId - ID de la permission
   * @returns {Promise<Object>} - Résultat de l'opération
   */
  revokePermissionFromRole: async (roleId, permissionId) => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');

      const response = await axios.delete(
          `${API_URL}/roles/${roleId}/permissions/${permissionId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
      );

      return response.data;
    } catch (error) {
      console.error(`Error revoking permission from role:`, error);
      throw error;
    }
  },

  /**
   * Attribuer un rôle à un utilisateur
   * @param {string} userId - ID de l'utilisateur
   * @param {string} roleId - ID du rôle
   * @returns {Promise<Object>} - Résultat de l'opération
   */
  assignRoleToUser: async (userId, roleId) => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');

      const response = await axios.post(
          `${API_URL}/roles/assign`,
          { userId, roleId },
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
      );

      return response.data;
    } catch (error) {
      console.error(`Error assigning role to user:`, error);
      throw error;
    }
  },

  /**
   * Révoquer un rôle d'un utilisateur
   * @param {string} userId - ID de l'utilisateur
   * @param {string} roleId - ID du rôle
   * @returns {Promise<Object>} - Résultat de l'opération
   */
  revokeRoleFromUser: async (userId, roleId) => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');

      const response = await axios.delete(
          `${API_URL}/roles/revoke/${userId}/${roleId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
      );

      return response.data;
    } catch (error) {
      console.error(`Error revoking role from user:`, error);
      throw error;
    }
  },

  /**
   * Récupérer les utilisateurs ayant un rôle spécifique
   * @param {string} roleId - ID du rôle
   * @returns {Promise<Array>} - Liste des utilisateurs
   */
  getRoleUsers: async (roleId) => {
    try {
      const token = getAuthToken();
      if (!token) return [];

      const response = await axios.get(
          `${API_URL}/roles/${roleId}/users`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
      );

      return response.data.users || [];
    } catch (error) {
      console.error(`Error getting users for role ${roleId}:`, error);
      return [];
    }
  },

  /**
   * Récupérer les permissions d'un rôle
   * @param {string} roleId - ID du rôle
   * @returns {Promise<Array>} - Liste des permissions
   */
  getRolePermissions: async (roleId) => {
    try {
      const token = getAuthToken();
      if (!token) return [];

      // Récupérer le rôle avec ses permissions
      const role = await roleService.getRole(roleId);
      return role.directPermissions || [];
    } catch (error) {
      console.error(`Error getting permissions for role ${roleId}:`, error);
      return [];
    }
  },

  /**
   * Récupérer les rôles système
   * @returns {Promise<Array>} - Liste des rôles système
   */
  getSystemRoles: async () => {
    try {
      const roles = await roleService.getAllRoles();
      return roles.filter(role => role.isSystem);
    } catch (error) {
      console.error('Error getting system roles:', error);
      return [];
    }
  },

  /**
   * Récupérer les rôles personnalisés
   * @returns {Promise<Array>} - Liste des rôles personnalisés
   */
  getCustomRoles: async () => {
    try {
      const roles = await roleService.getAllRoles();
      return roles.filter(role => !role.isSystem);
    } catch (error) {
      console.error('Error getting custom roles:', error);
      return [];
    }
  },

  /**
   * Récupérer les rôles actifs
   * @returns {Promise<Array>} - Liste des rôles actifs
   */
  getActiveRoles: async () => {
    try {
      const roles = await roleService.getAllRoles();
      return roles.filter(role => role.isActive);
    } catch (error) {
      console.error('Error getting active roles:', error);
      return [];
    }
  }
};

export default roleService;